/* import __COLOCATED_TEMPLATE__ from './slack-teammate-auth-banner.hbs'; */
/* RESPONSIBLE TEAM: team-omni */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { request } from 'embercom/lib/inbox/requests';
import ENV from 'embercom/config/environment';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import type SlackIntegrationService from 'embercom/services/slack-integration-service';

enum BannerState {
  None = 'none',
  Regular = 'regular',
  Stale = 'stale',
}

interface Signature {
  Args: {
    conversation: any;
  };
}

export default class SlackTeammateAuthBanner extends Component<Signature> {
  @service declare session: Session;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare slackIntegrationService: SlackIntegrationService;
  @tracked bannerState = BannerState.None;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.fetchUserToken();
    this.subscribeToAuthCallback();
  }

  get shouldShowBanner(): boolean {
    return this.bannerState !== BannerState.None;
  }

  get isStale(): boolean {
    return this.bannerState === BannerState.Stale;
  }

  private get slackIntegrationAttribute() {
    return this.args.conversation.attributes.find(
      (attribute: any) => attribute.descriptor.id === 'slack_workspace',
    );
  }

  private async fetchUserToken() {
    let isFeatureEnabled = this.session.workspace.isFeatureEnabled('team-omni-slack');

    if (!isFeatureEnabled) {
      return;
    }

    let slackIntegrationAttribute = this.slackIntegrationAttribute;

    if (!slackIntegrationAttribute) {
      return;
    }

    let slackIntegrationId = slackIntegrationAttribute.value;

    let response = await request(
      `/ember/slack/v2/user/oauth_token?app_id=${this.session.workspace.id}&slack_integration_id=${slackIntegrationId}`,
    );

    let result = await response.json();

    if (result === null) {
      this.bannerState = BannerState.Regular;
    } else if (result.stale === true) {
      this.bannerState = BannerState.Stale;
    } else {
      this.bannerState = BannerState.None;
    }
  }

  @action async authenticate() {
    let slackIntegrationId = this.slackIntegrationAttribute?.value;

    let requestUrl = `/ember/slack/v2/user/install?app_id=${this.session.workspace.id}`;
    if (slackIntegrationId) {
      requestUrl += `&slack_integration_id=${slackIntegrationId}`;
    }

    let data = await request(requestUrl).then((response) => response.json());
    this.openSlackOAuthWindow(data.url);
  }

  private subscribeToAuthCallback() {
    window.addEventListener('message', (event) => {
      if (ENV.environment !== 'development' && event.origin !== window.location.origin) {
        return;
      }
      if (event.data.type === 'slack-auth-complete') {
        this.fetchUserToken();
      }
    });
  }

  private openSlackOAuthWindow(url: string) {
    let width = 600;
    let height = 800;
    let left = screen.width / 2 - width / 2;
    let top = screen.height / 2 - height / 2;

    safeWindowOpen(
      url,
      'slack-oauth',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::SlackTeammateAuthBanner': typeof SlackTeammateAuthBanner;
    'inbox2/slack-teammate-auth-banner': typeof SlackTeammateAuthBanner;
  }
}
